/**
 * Where a person goes once they are signed in — the ONE answer for every
 * login path (password, passkey, MFA, magic link, signup, GitHub, and the
 * login page forwarding a visitor who was already signed in).
 *
 * The `?redirect` value comes from proxy.ts (a gated visit to /app/…) or
 * from forceSignOut (the page the session died on). It is a same-origin
 * PATH or nothing: a value that is not a rooted path, or that is a
 * protocol-relative URL (`//evil.example`), or that carries a backslash
 * (browsers normalise `/\evil.example` to a host), is dropped and the
 * person lands on /app. Six call sites used to reimplement this check
 * with `startsWith("/")`, which let `//host` through.
 */

export const DEFAULT_POST_LOGIN_PATH = "/app";

export function safeRedirectPath(value: string | null | undefined): string | null {
  if (!value) return null;
  if (!value.startsWith("/") || value.startsWith("//") || value.includes("\\")) return null;
  return value;
}

/** The target for a `?redirect=` query string (`window.location.search`). */
export function postLoginTarget(search: string): string {
  const redirect = new URLSearchParams(search).get("redirect");
  return safeRedirectPath(redirect) ?? DEFAULT_POST_LOGIN_PATH;
}

/**
 * Carry the current `?redirect` onto the next hop of a multi-step login
 * (the MFA challenge, the GitHub start door). Empty when there is nothing
 * safe to carry.
 */
export function redirectQuery(search: string): string {
  const path = safeRedirectPath(new URLSearchParams(search).get("redirect"));
  return path ? `?redirect=${encodeURIComponent(path)}` : "";
}
